import { Info } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";
import { EnvironmentalFootprintCards } from "../../components/environmentalFootprint";
import { Badge } from "../../components/ui/common/badge";
import { Card, CardContent } from "../../components/ui/common/card";
import { MessageAlert } from "../../components/ui/common/message-alert";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON, NOT_APPLICABLE } from "../../config/constants";
import { humanize } from "../../utils/utils";
import { EmptyState, ProductSelector, ProductSummaryHeader } from "./components";
import { ACTION_BUTTONS } from "./constants";
import { processLifecycleStages } from "./helpers";
import {
  useCalculatedDate,
  useProcessingSteps,
  useProductSummaryData,
  useProductSummaryEffects,
  useProductSummaryState
} from "./hooks/useProductSummary";
import "./productSummary.scss";
import ProductSummaryDetailModal from "./productSummaryDetails/ProductSummaryDetailsModal";
import ProductSummaryHistorySheet from "./productSummaryHistory/ProductSummaryHistorySheet";
import { DropdownSalesUnitOption, LifecycleStage } from "./types";

/**
 * ProductSummary Component
 * Refactored following SOLID principles:
 * - Single Responsibility: Main component only orchestrates child components
 * - Open/Closed: Easy to extend with new action buttons or cards
 * - Dependency Inversion: Depends on abstractions (hooks, components)
 */
const ProductSummary = () => {
  // State management (extracted to custom hook)
  const state = useProductSummaryState();

  const form = useForm({
    defaultValues: {
      salesUnitId: state.salesUnitId || ""
    }
  });

  const navigate = useNavigate();

  // Data fetching (extracted to custom hook)
  const {
    productSalesUnitsData,
    isProductSalesUnitsLoading,
    productSummaryDropdownData,
    isProductSummaryHistoryLoading,
    isAverageProductSummaryLoading,
    isProductSummaryDataLoading,
    productSummariesForNode,
    refetchProductSummaryDropdownData,
    refetchAverageProductSummary,
    refetchProductSummaryHistoryById
  } = useProductSummaryData(
    state.productId,
    state.salesUnitId,
    state.productSummaryId,
    state.isAverageProduct,
    state.searchKey,
    state.setProductSummary
  );

  // Computed data (extracted to custom hooks)
  const calculatedDate = useCalculatedDate(productSummaryDropdownData);

  // Process lifecycle stages with cultivation impact added to ingredient impact
  const lifecycleStages: LifecycleStage[] = useMemo(() => {
    const cycleStageData = state.productSummary?.cycleStages || {};
    return processLifecycleStages(cycleStageData, NOT_APPLICABLE, humanize);
  }, [state.productSummary]);

  const processingSteps = useProcessingSteps(productSummariesForNode);

  // Side effects (extracted to custom hook)
  useProductSummaryEffects(
    state.salesUnitId,
    state.productId,
    state.isAverageProduct,
    state.productSummary,
    productSalesUnitsData,
    productSummaryDropdownData,
    state.setSalesUnitId,
    state.setProductId,
    state.setIsAverageProduct,
    state.setProductSummaryId,
    state.setProductSummaryErrors,
    state.setProductSummaryWarnings
  );

  // Update form when salesUnitId changes
  useEffect(() => {
    if (state.salesUnitId) {
      form.setValue("salesUnitId", state.salesUnitId);
    }
  }, [state.salesUnitId, form]);

  // Loading state check
  if (
    state.productId &&
    state.salesUnitId &&
    (isProductSummaryHistoryLoading ||
      isProductSummaryDataLoading ||
      isAverageProductSummaryLoading)
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  // Prepare dropdown data
  const calculatedDateData =
    productSummaryDropdownData?.data?.map((item: any) => {
      let text = "";
      if (item?.createdAt) {
        const d = new Date(item.createdAt);
        if (!isNaN(d.getTime())) {
          text = d.toISOString().slice(0, 19).replace("T", " ");
        }
      }
      return {
        key: item._id,
        value: item._id,
        text
      };
    }) || [];

  const productSalesUnits: DropdownSalesUnitOption[] = (productSalesUnitsData || []).map(
    ({ productName, salesUnitName, salesUnitId, isAverageProduct }) => ({
      key: salesUnitId,
      text: `${productName} - ${salesUnitName}`,
      value: salesUnitId,
      isAverageProduct: Boolean(isAverageProduct)
    })
  );

  const formSelectOptions = productSalesUnits.map(option => ({
    id: option.value,
    title: option.text,
    value: option.value
  }));

  // Handler for product selection change
  const handleProductChange = (value: string) => {
    state.setSalesUnitId(value);
    const productData = productSalesUnitsData?.find(
      productSalesUnit => productSalesUnit.salesUnitId === value
    );
    const productId = productData?.productId;
    const isAverageProduct = productData?.isAverageProduct || false;
    state.setProductId(productId);
    state.setIsAverageProduct(isAverageProduct);
  };

  // Empty state when no data
  if (!calculatedDateData.length && !state.isAverageProduct) {
    return (
      <div className="tw-min-h-screen tw-bg-white">
        <EmptyState
          form={form}
          options={formSelectOptions}
          isLoading={isProductSalesUnitsLoading}
          onChangeValue={handleProductChange}
        />
      </div>
    );
  }

  // Extract product details
  const { product_name: productName, product_image_path: productImage } =
    productSummaryDropdownData?.productDetail || {};

  const { measure: climateChangeMeasure, unit: climateChangeUnit } =
    state.productSummary?.climateChange || {};

  const totalImpact = state.productSummary?.totalImpact || [];

  // Handler for action buttons
  const handleButtonAction = (id: number) => {
    const actionButtonOptions: Record<number, () => void> = {
      7: () =>
        navigate(
          `/product-summary/suggestions?sourceId=${state.salesUnitId}&productId=${state.productId}`
        ),
      8: () => navigate(`/mapping/product?sourceId=${state.salesUnitId}`),
      9: () =>
        navigate(
          `/simulations/product-simulation?salesUnitId=${state.salesUnitId}&productId=${state.productId}`
        ),
      11: () => state.setProductSummaryHistoryModal(true)
    };
    const action = actionButtonOptions[id];
    if (action) {
      action();
    }
  };

  return (
    <div className="tw-p-1 tw-mx-auto tw-space-y-6 ">
      {/* Page header: product selector + action links, same row */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-border-b tw-pb-4">
        <div>
          <span className="tw-text-sm tw-uppercase tw-text-muted-foreground tw-font-semibold tw-font-mono tw-mb-5">
            product
          </span>
          <ProductSelector
            form={form}
            options={formSelectOptions}
            isLoading={isProductSalesUnitsLoading}
            onChangeValue={handleProductChange}
          />
        </div>
        <ProductSummaryHeader
          actionButtons={ACTION_BUTTONS}
          showButtons={!state.isAverageProduct && !DISABLE_IS_ROOT_PRODUCT_BUTTON}
          onButtonAction={handleButtonAction}
        />
      </div>

      {/* Section title */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <div>
          <h1 className="tw-text-2xl tw-font-display tw-font-bold tw-text-primary tw-leading-tight">
            Product Summary
          </h1>
          <p className="tw-text-xs tw-font-medium tw-text-muted-foreground tw-mt-0.5">
            Lifecycle impact and environmental categories.
          </p>
        </div>
        <Badge variant="outline" className="tw-rounded-md tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm">
          Per kg output
        </Badge>
      </div>

      {/* Error and Warning Messages */}
      <MessageAlert
        variant="error"
        title="Errors"
        messages={state.productSummaryErrors}
        onDismiss={() => state.setProductSummaryErrors([])}
      />
      <MessageAlert
        variant="warning"
        title="Warnings"
        messages={state.productSummaryWarnings}
        onDismiss={() => state.setProductSummaryWarnings([])}
      />

      {/* Main Content Cards */}
      <EnvironmentalFootprintCards
        productImage={productImage}
        productName={productName}
        calculatedDate={calculatedDate}
        isAverageProduct={state.isAverageProduct}
        climateChangeMeasure={climateChangeMeasure}
        climateChangeUnit={climateChangeUnit}
        lifecycleStages={lifecycleStages}
        totalImpact={totalImpact}
        // middleContent={<ProcessingImpactsCard processingSteps={processingSteps} />} remove using this bug ticket NPCW-2879
      />

      {/* ISO Standards Information */}
      <Card className="tw-border-border tw-bg-secondary/20">
        <CardContent className="tw-p-1 tw-flex tw-items-start tw-gap-3">
          <Info className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-mt-0.5 tw-shrink-0" />
          <p className="tw-text-xs tw-text-muted-foreground tw-leading-relaxed">
            Impact assessment methodology strictly adheres to ISO 14040:2006 and ISO 14044:2006
            standards for Life Cycle Assessment. Results are calculated per functional unit (1 kg of
            product) using the EF 3.1 characterisation method.
          </p>
        </CardContent>
      </Card>

      {/* Modals */}
      <ProductSummaryDetailModal
        visibleModal={state.viewProductSummaryDetailsModal}
        setVisibleModal={() => state.setProductSummaryDetailsModal(false)}
      />
      {state.productSummaryHistoryModal && (
        <ProductSummaryHistorySheet
          visibleModal={state.productSummaryHistoryModal}
          onClose={() => state.setProductSummaryHistoryModal(false)}
          productId={state.productId}
          salesUnitId={state.salesUnitId}
          isAverageProduct={!!state.isAverageProduct}
          onVersionRestored={() => {
            refetchProductSummaryDropdownData();
            if (state.isAverageProduct) {
              refetchAverageProductSummary();
            } else {
              refetchProductSummaryHistoryById();
            }
          }}
        />
      )}
    </div>
  );
};

export default ProductSummary;
